import { Box, Button, Chip, CircularProgress, Grid, IconButton, Stack, TextareaAutosize, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DrawerComponent from "../../components/Drawer";
import { ArrowDownIcon, ArrowUpIcon, CloseLineIcon, FileUploadIcon, iconFontSize, NoteIcon, PlusIcon } from "../../components/IconComponent";
import { formatDateTime, formatDateToDDMMYYYYHHMMFrEpoch } from "../../utils/util";
import LinkComponent from "../../components/Link";
import { GetIconColor } from "../../theme/main_style";
import Modal from "../../components/Modal";
import InfiniteScroll from "react-infinite-scroll-component";
import ConfirmComponent from "../../components/Confirm";
import AddAttachment from "../../components/AddAttachment";
import AlertComponent from "../../components/Alert";
import CameraComponent from "../../components/CameraComponent";
import { commonFont } from "../../theme";
import CardComponent from "../../components/Card";

export default function ServiceRequestDetailView(props){
    const { t } = useTranslation();
    const { serviceRequest, serviceRequestNotes, files, showProcessing, message, newFiles, addAttachment} = props;
    const { palette } = useTheme();
    const [openAddNote, setOpenAddNote] = useState(false);
    const [newNote, setNewNote] = useState(null);
    const maximumFilesNumber = 3;
    const [showCamera, setShowCamera] = useState(false);

    const processContentWithURL = (content) => {
        const isHTML = (str) => !(str || '')
            // replace html tag with content
            .replace(/<([^>]+?)([^>]*?)>(.*?)<\/\1>/ig, '')
            // remove remaining self closing tags
            .replace(/(<([^>]+)>)/ig, '')
            // remove extra space at start and end
            .trim();
        if (content && !isHTML(content)) {
            let urlRegex = /(https?:\/\/[^\s]+)/g;
            content = content.replace(urlRegex, function (url) {
                return '<a href="' + url + '">' + url + '</a>';
            })
        }
        if(content && content.length > 0){
            content = content.replaceAll(/\n/g, '<br>')
        }
        return content;
    }

    const onClickAddAttachment = () => {
        props.setAddAttachment(true);
    }

    let allowAddAttachment = true;
    let myFiles = files && files.length>0 ? files[0].files : null;
    if(myFiles && myFiles.length >= maximumFilesNumber){
        allowAddAttachment = false;
    }

    return(
        <>
            <DrawerComponent showProcessing={showProcessing} isOpen={props.open} onClose={props.onClose} title={t('service_request')} headerPosition='relative' width='100%'>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{serviceRequest.number}</Typography>
                        <Typography variant="subtitle2" sx={{opacity:0.7}}>{serviceRequest && serviceRequest.created_date ? formatDateTime(serviceRequest.created_date) : undefined}</Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle2" >{t('classification')}</Typography>
                    <Chip sx={{ borderRadius: 2 , marginLeft: 2}} size="small" label={serviceRequest.classification}/>
                </Stack>
                <Stack direction="row" alignItems="center"  mt={2}>
                    <Typography variant="subtitle2">{t('state')}</Typography>
                    <Chip sx={{ borderRadius: 2, marginLeft: 2 }} size="small" label={serviceRequest.stage?.name} style={{ backgroundColor: serviceRequest.stage?.colour }} />
                </Stack>
                
                {serviceRequest.stage.name === 'Closed' && serviceRequest.closure_reason && 
                <Stack direction="row" alignItems="center">
                    <Typography variant="subtitle2">{t('closure_reason')}</Typography>
                    <Typography variant="subtitle2">{serviceRequest.closure_reason && serviceRequest.closure_reason.name ? serviceRequest.closure_reason.name : ''}</Typography>
                </Stack>
                }
                {serviceRequest.response && <Grid item xs={12} mt={1}>
                        <Typography fontWeight={'bold'}>{t('response')}</Typography>
                        <Typography variant="subtitle2">{serviceRequest.response}</Typography>
                </Grid>}

                <Grid item xs={12} mt={2}>
                    <Typography fontWeight={'bold'}>{t('description')}</Typography>
                    <Typography variant="subtitle2">{serviceRequest.description}</Typography>
                </Grid>

                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
                    <Typography fontWeight={'bold'}>{t('attachments')}</Typography>
                    <IconButton sx={{ zIndex: 1, 
                        backgroundColor: allowAddAttachment ?  palette.primary.main : palette.primary.light,
                        padding: "2px", 
                        cursor : allowAddAttachment ? 'pointer' : 'initial',
                    ':hover': {
                        backgroundColor: allowAddAttachment ?  palette.primary.main : palette.primary.light,
                    }}}
                    onClick={() => allowAddAttachment && onClickAddAttachment() }
                >
                    <PlusIcon color={allowAddAttachment ? GetIconColor().white : GetIconColor().default} size={iconFontSize.xs}/>
                    </IconButton>
                </Stack>
                
                {files && files.length > 0 && 
                    <Grid item xs={12}>
                        {files.map((itemFile, index) => {
                            if(itemFile && itemFile.files && itemFile.files.length > 0){
                                return itemFile.files.map((file, index) => {
                                    return (
                                        <Box key={index} display="flex" alignItems="center" gap={1}>
                                            <LinkComponent onClick={() => props.onOpenFile(file.file)} style={{ textDecoration: 'none' }}>
                                                {file?.file?.name && (
                                                <Typography variant="body2" color={palette.primary.main}>
                                                    {file.file.name}
                                                </Typography>
                                                )}
                                            </LinkComponent>
                                            <Box onClick={() => props.onDeleteFile(file)} sx={{ cursor: 'pointer' }}>
                                                <CloseLineIcon size={iconFontSize.xs} />
                                            </Box>
                                        </Box>
                                    )
                                })
                            }
                            
                        })}
                </Grid>}

                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
                    <Typography fontWeight={'bold'}>{t('service_request_notes')}</Typography>
                    <IconButton sx={{ zIndex: 1, backgroundColor: palette.primary.main, padding: "2px", 
                    ':hover': {
                        backgroundColor: palette.primary.main,
                    }}}
                    onClick={() => {setOpenAddNote(true)}}
                ><PlusIcon color={GetIconColor().white} size={iconFontSize.xs} /></IconButton>
                </Stack>
                {serviceRequest && serviceRequestNotes && serviceRequestNotes.content.length > 0 && <Grid item xs={12} mt={2}>
                    <Box
                        id="scrollableDiv"
                        sx={{
                            height: '600px', 
                            overflowY: 'auto'
                        }}
                        >

                    <InfiniteScroll
                        hasMore={serviceRequestNotes.paging.has_more}
                        dataLength={serviceRequestNotes.content.length}
                        next={props.handleLoadMore}
                        scrollableTarget="scrollableDiv"
                        loader={props.showLoadMoreServiceRequests ? <center><CircularProgress size={'1.25rem'} /></center> : null}
                    >
                        {serviceRequestNotes && serviceRequestNotes.content && serviceRequestNotes.content.map((itemNote, index) => {
                            let short_note = itemNote.note ? processContentWithURL(itemNote.note).replaceAll('\n', '<br/>').slice(0, 200) : null;
                            let hasMore = false;
                            if(short_note && itemNote.note.length > 200){
                                short_note += '...';
                                hasMore = true;
                            }
                            return (
                                <Box
                                    key={index}
                                    my={1}
                                    px={2}
                                    py={1}
                                    width="100%"
                                    borderRadius={2}
                                    border={`1px solid ${palette.border.main}`}
                                    onClick={() => props.onShowAllNote(itemNote)}
                                >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Stack
                                        alignItems="center"
                                        spacing={0}
                                        sx={{ minHeight: '100%' }}
                                    >
                                        <IconButton sx={{ zIndex: 1, backgroundColor: palette.primary.main, cursor:'default',
                                            ':hover': {
                                                backgroundColor: palette.primary.main,
                                            },
                                        }}>
                                            <NoteIcon color={GetIconColor().white} size={iconFontSize.xs} />
                                        </IconButton>
                                </Stack>

                                <Stack spacing={0.5}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Stack direction="row" spacing={1}>
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>{itemNote.created_by.name}</Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.7 }}>{formatDateToDDMMYYYYHHMMFrEpoch(itemNote.created_on)}</Typography>
                                        </Stack>
                                        {hasMore && itemNote.showAllNote &&
                                            <ArrowUpIcon color={GetIconColor().primary} size={iconFontSize.xs} /> }

                                        {hasMore && !itemNote.showAllNote &&
                                            <ArrowDownIcon color={GetIconColor().primary} size={iconFontSize.xs} />
                                        }
                                    </Box>

                                    <Stack flex={1}  sx={{ cursor: 'pointer' }}>
                                        {short_note && !itemNote.showAllNote && 
                                            <Typography mt={2} variant="subtitle2" dangerouslySetInnerHTML={{ __html: short_note }}/>
                                        }

                                        {itemNote.showAllNote && 
                                            <Typography mt={2} variant="subtitle2" dangerouslySetInnerHTML={{ __html: processContentWithURL(itemNote.note) }}/>
                                        }
                                    </Stack>
                                </Stack>

                                </Stack>
                            </Box>
                            )
                        })}
                    </InfiniteScroll>
                    </Box>
                </Grid>}

                {openAddNote && 
                    <Modal enableCloseButton isOpen={true} onClose={() => setOpenAddNote(false)} title={t("add_note")} maxWidth={'sm'}>
                        <Stack spacing={2} mt={3}>
                        <TextareaAutosize
                            id="outlined-controlled"
                            minRows={1}
                            placeholder={t("add_your_note_here")}
                            value={newNote}
                            onChange={(event) => {
                                setNewNote(event.target.value);
                            }}
                            style={{
                                fontFamily: commonFont.regular,
                                padding: 20
                            }}
                            />
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="flex-end" mt={2}>
                            <Button sx={{width:'auto'}} justifyContent id='addEditPeople/btnSubmit' variant='contained' 
                                onClick={() => {
                                    props.onSubmitAddNote(newNote, serviceRequest.id);
                                    setNewNote(null)
                                    setOpenAddNote(false)                           
                                }}>{t('save')}</Button>
                        </Stack>
                    </Modal>
                }

                {addAttachment && files.length < maximumFilesNumber ? 
                    <Modal enableCloseButton isOpen={addAttachment} onClose={() => props.setAddAttachment(false)} title={t("upload_files")} maxWidth={'sm'}>
                        <AddAttachment chooseFromGaleryTitle={t('from_gallery')} onUploadFile={props.uploadFiles} setShowCamera={setShowCamera} isMultipleFile={true} files={newFiles} maximumFilesNumber={maximumFilesNumber} setMessage={props.setMessage} fromServiceRequestAddAttachment={true} icon={FileUploadIcon} removeFile={props.removeFile} takeAPhoto={true}/>                        
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button fullWidth id="confirm/btnOK" variant="contained" onClick={() => props.onSaveFilesState()}>{'OK'}</Button>
                            <Button fullWidth id="confirm/btnCancel" variant="outlined" onClick={() => props.setAddAttachment(false)}>{'Cancel'}</Button>
                        </Box>
                    </Modal>
                    : <>
                    {files.length >= maximumFilesNumber && message && message.show && <AlertComponent
                        isOpen={message.show}
                        message={message.content}
                        otherMessage={message.otherMessage ? message.otherMessage : null}
                        onClose={message.callBackFn}
                    />}
                    </>
                    }

                {showCamera && <CameraComponent/>}
                    
            </DrawerComponent>
        </>
    )
}